import React from 'react';
import { 
  SquareIcon,
  Ruler,
  ClipboardList,
  CreditCard,
  ArrowRight,
  FileText,
  LayoutPanelTop,
  Layers,
  Home
} from 'lucide-react';
import ToolResult, { ResultItem } from '../../../../../components/ui/ToolResult';
import Button from '../../../../../components/ui/Button';
import { formatNumber, formatCurrency } from '../../../../../utils/formatting';

/**
 * Компонент для отображения результатов расчета площади
 */
const ResultsOverview = ({ results, formData, setShowMaterialCalculator }) => {
  if (!results) return null;
  
  // Получаем размерность по выбранной единице измерения
  const measurementUnit = formData.measurementUnit;
  const unitSymbol = measurementUnit === 'meters' ? 'м' : measurementUnit === 'centimeters' ? 'см' : 'мм';

  // Форматирование числа с учетом выбранной единицы измерения
  const formatArea = (value) => {
    let formattedValue = value;
    
    // Если не метры, конвертируем
    if (measurementUnit === 'centimeters') {
      formattedValue = value * 10000; // м² в см²
    } else if (measurementUnit === 'millimeters') {
      formattedValue = value * 1000000; // м² в мм²
    }
    
    return `${formatNumber(formattedValue, 2)} ${unitSymbol}²`;
  };
  
  // Форматирование длины с учетом выбранной единицы измерения
  const formatLength = (value) => {
    let formattedValue = value;
    
    // Если не метры, конвертируем
    if (measurementUnit === 'centimeters') {
      formattedValue = value * 100; // м в см
    } else if (measurementUnit === 'millimeters') {
      formattedValue = value * 1000; // м в мм
    }
    
    return `${formatNumber(formattedValue, 2)} ${unitSymbol}`;
  };
  
  // Форматирование валюты (в рублях)
  const formatRubles = (value) => {
    return `${formatCurrency(value, 0)} ₽`;
  };

  return (
    <ToolResult title="Результаты расчета площади помещения">
      {/* Ключевые метрики */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-6">
        {/* Площадь пола */}
        <div className="rounded-lg bg-gradient-to-br from-fuchsia-50 to-purple-50 dark:from-fuchsia-900/30 dark:to-purple-900/30 p-5 border border-fuchsia-100 dark:border-fuchsia-800/50 shadow-sm">
          <h3 className="text-base font-medium text-fuchsia-700 dark:text-fuchsia-300 mb-1.5 flex items-center gap-1.5">
            <SquareIcon className="w-4 h-4"/>
            Площадь пола
          </h3>
          <div className="flex items-baseline flex-wrap gap-x-2">
            <span className="text-3xl font-bold text-fuchsia-800 dark:text-fuchsia-200" aria-live="polite">
              {formatArea(results.floorArea)}
            </span>
          </div>
          <p className="text-xs text-fuchsia-600 dark:text-fuchsia-400 mt-1">
            Площадь напольного покрытия
          </p>
        </div>

        {/* Площадь стен */}
        <div className="rounded-lg bg-gradient-to-br from-blue-50 to-sky-50 dark:from-blue-900/30 dark:to-sky-900/30 p-5 border border-blue-100 dark:border-blue-800/50 shadow-sm">
          <h3 className="text-base font-medium text-blue-700 dark:text-blue-300 mb-1.5 flex items-center gap-1.5">
            <Layers className="w-4 h-4"/>
            Площадь стен
          </h3>
          <div className="flex items-baseline flex-wrap gap-x-2">
            <span className="text-3xl font-bold text-blue-800 dark:text-blue-200" aria-live="polite">
              {formatArea(results.wallArea)}
            </span>
          </div>
          <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">
            Площадь стен (минус проемы)
          </p>
        </div>

        {/* Общая смета */}
        <div className="rounded-lg bg-gradient-to-br from-emerald-50 to-green-50 dark:from-emerald-900/30 dark:to-green-900/30 p-5 border border-emerald-100 dark:border-emerald-800/50 shadow-sm">
          <h3 className="text-base font-medium text-emerald-700 dark:text-emerald-300 mb-1.5 flex items-center gap-1.5">
            <CreditCard className="w-4 h-4"/>
            Стоимость материалов
          </h3>
          <div className="flex items-baseline flex-wrap gap-x-2">
            <span className="text-3xl font-bold text-emerald-800 dark:text-emerald-200" aria-live="polite">
              {formatRubles(results.totalMaterialCost)}
            </span>
          </div>
          <p className="text-xs text-emerald-600 dark:text-emerald-400 mt-1">
            Общая стоимость отделочных материалов
          </p>
        </div>
      </div>

      {/* Детализированные результаты */}
      <div className="space-y-3 mb-6">
        <ResultItem 
          label="Периметр помещения" 
          value={formatLength(results.wallPerimeter)} 
          color="fuchsia" 
          icon={<Ruler className="w-4 h-4"/>}
        />
        
        <ResultItem 
          label="Площадь потолка" 
          value={formatArea(results.ceilingArea)} 
          color="sky" 
          icon={<LayoutPanelTop className="w-4 h-4"/>}
        />
        
        <ResultItem 
          label="Общая площадь поверхностей" 
          value={formatArea(results.totalArea)} 
          icon={<ClipboardList className="w-4 h-4"/>} 
        />
        
        {results.balconyArea > 0 && (
          <ResultItem 
            label="Площадь балкона/лоджии" 
            value={formatArea(results.balconyArea)} 
            color="amber"
            icon={<Home className="w-4 h-4"/>} 
          />
        )}

        {results.openingsArea > 0 && (
          <ResultItem 
            label="Площадь проемов (окна и двери)" 
            value={formatArea(results.openingsArea)} 
            color="gray" 
          />
        )}
        
        {results.skirtingLength > 0 && (
          <ResultItem 
            label="Длина плинтуса" 
            value={formatLength(results.skirtingLength)} 
            color="gray" 
          />
        )}
        
        <ResultItem 
          label="Высота потолка" 
          value={formatLength(results.ceilingHeight)} 
          color="gray" 
        />
        
        <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
          <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-3 flex items-center">
            <CreditCard className="w-4 h-4 mr-2 text-fuchsia-500" />
            Стоимость материалов
          </h4>
          
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="bg-gray-50 dark:bg-gray-800 p-3 rounded-lg border border-gray-200 dark:border-gray-700">
              <p className="text-xs text-gray-500 dark:text-gray-400">Напольное покрытие:</p>
              <p className="text-sm font-medium text-gray-900 dark:text-white">{formatRubles(results.flooringCost)}</p>
            </div>
            
            <div className="bg-gray-50 dark:bg-gray-800 p-3 rounded-lg border border-gray-200 dark:border-gray-700">
              <p className="text-xs text-gray-500 dark:text-gray-400">Отделка стен:</p>
              <p className="text-sm font-medium text-gray-900 dark:text-white">{formatRubles(results.wallMaterialCost)}</p>
            </div>
            
            <div className="bg-gray-50 dark:bg-gray-800 p-3 rounded-lg border border-gray-200 dark:border-gray-700">
              <p className="text-xs text-gray-500 dark:text-gray-400">Отделка потолка:</p>
              <p className="text-sm font-medium text-gray-900 dark:text-white">{formatRubles(results.ceilingMaterialCost)}</p>
            </div>
          </div>
        </div>

        {/* Информация о типе помещения */}
        <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4 mt-4 border border-gray-200 dark:border-gray-700">
          <div className="flex items-start gap-3">
            <div className="mt-0.5">
              <FileText className="w-5 h-5 text-fuchsia-500" />
            </div>
            <div>
              <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-1">Информация о помещении</h4>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Форма: <span className="font-medium text-fuchsia-600 dark:text-fuchsia-400">
                  {formData.roomShape === 'rectangle' ? 'Прямоугольная' : 
                   formData.roomShape === 'l-shaped' ? 'Г-образная' : 
                   formData.roomShape === 'angled' ? 'Со скошенным углом' :
                   formData.roomShape === 'bay-window' ? 'С эркером' :
                   formData.roomShape === 'niche' ? 'С нишей' :
                   formData.roomShape === 'column' ? 'С колонной' :
                   formData.roomShape === 'polygon' ? 'Произвольная форма' : 'Круглая'}
                </span>. 
                Детали расчета и рекомендации по ремонту доступны в расширенном отчете.
              </p>
            </div>
          </div>
        </div>
      </div>
      
      {/* Кнопка для перехода к расчету материалов */}
      <div className="mt-6 flex justify-center">
        <Button
          onClick={() => setShowMaterialCalculator(true)}
          primary
          icon={<ArrowRight className="w-4 h-4" />}
        >
          Рассчитать необходимые материалы
        </Button>
      </div>
    </ToolResult>
  );
};

export default ResultsOverview;